import {memo, useEffect, useState} from 'react';
import Loader from '../MLS/Loader/Loader';

interface EmailOtpModalProps {
  email: string;
  code: string;
  onCodeChange: (code: string) => void;
  error?: string;
  isSending: boolean;
  isLoading: boolean;
  resendSuccessMessage?: string;
  resendCooldownSeconds: number;
  onVerify: () => void;
  onCancel: () => void;
  onResend: () => void;
}

export const EmailOtpModal = memo(
  ({
    email,
    code,
    onCodeChange,
    error,
    isSending,
    isLoading,
    resendSuccessMessage,
    resendCooldownSeconds,
    onVerify,
    onCancel,
    onResend,
  }: EmailOtpModalProps) => {
    const [cooldownSeconds, setCooldownSeconds] = useState(resendCooldownSeconds);

    useEffect(() => {
      setCooldownSeconds(resendCooldownSeconds);
    }, [resendCooldownSeconds]);

    useEffect(() => {
      if (cooldownSeconds <= 0) {
        return;
      }

      const timer = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }, [cooldownSeconds]);

    const canResend = cooldownSeconds === 0 && !isSending;
    const minutes = Math.floor(cooldownSeconds / 60);
    const seconds = cooldownSeconds % 60;
    const cooldownText =
      cooldownSeconds > 0 ? `Resend code (${minutes}:${seconds.toString().padStart(2, '0')})` : 'Resend code';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
        <div className="w-full max-w-xl rounded-lg bg-white p-6 text-black shadow-xl">
          <h2 className="text-2xl font-semibold text-center">Check your email</h2>
          <p className="mt-2 text-sm text-center text-gray-700">
            We sent a 6-digit code to {email}. Enter it below to continue.
          </p>
          <div className="mt-4 flex flex-col gap-3">
            <input
              className="w-full rounded border px-3 py-2 text-base"
              value={code}
              onChange={(e) => onCodeChange(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              inputMode="numeric"
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            {resendSuccessMessage && <p className="text-sm text-green-600">{resendSuccessMessage}</p>}

            {isLoading ? <Loader size="sm" /> : null}
            <div className="flex gap-3 mt-2">
              <button
                className="flex-1 rounded-md border border-gray-300 py-2 text-gray-700 text-lg"
                onClick={onCancel}
              >
                Cancel
              </button>

              <button
                className="flex-1 rounded-md bg-primary py-2 text-white disabled:opacity-60 text-lg"
                disabled={code.length !== 6 || isSending || isLoading}
                onClick={onVerify}
              >
                {isLoading ? 'Verifying..' : 'Verify'}
              </button>
            </div>
            <button
              className={`text-sm text-primary underline disabled:opacity-60 ${
                canResend ? 'cursor-pointer text-blue-900' : 'cursor-not-allowed'
              }`}
              disabled={!canResend}
              onClick={onResend}
            >
              {cooldownText}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

EmailOtpModal.displayName = 'EmailOtpModal';
